import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { aiaAwardsQueryOptions, type AiaAward } from "@/lib/aia-awards.queries";
import { boardRosterQueryOptions } from "@/lib/board-snippet.queries";
import { type AiaMember, formatFullName } from "@/lib/aia.queries";
import { formatPrice, formatPublishDate, type ResearchProduct } from "@/lib/store.queries";
import type { NewsArticle } from "@/lib/news.queries";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { StoredLink } from "@/components/site/StoredLink";
import { FramedPicture } from "@/components/site/FramedPicture";
import { cn, noWidow } from "@/lib/utils";
import { articleHref, formatArticleDate } from "../-welcome/data";
import leadStoryImg from "@/assets/welcome/v2-lead-story.jpg";

/**
 * Pieces shared by the two /homepages candidates. The layout vocabulary is the
 * /sample showcase (rule + kicker section headings, editorial cards, rounded
 * media tiles, pill CTAs); the copy is the /welcome canon in -welcome/content.ts.
 */

/* ------------------------------------------------------------- versions */

export type HomepageVersion = 1 | 2;

export const VERSIONS: { id: HomepageVersion; label: string; hint: string }[] = [
  { id: 1, label: "Slideshow", hint: "Current hero slideshow, refreshed layout" },
  { id: 2, label: "Video", hint: "Video hero, research-first" },
];

export const VERSION_DEFAULT: HomepageVersion = 1;

export function isVersion(v: unknown): v is HomepageVersion {
  return v === 1 || v === 2;
}

/**
 * The candidate switcher, pinned to the top-right of whichever hero is showing
 * (the /sample page pins its video picker the same way). Top, not bottom: the
 * slideshow's glass panel and its controls own the lower edge of the hero.
 */
export function VersionSwitcher({
  version,
  onSelect,
}: {
  version: HomepageVersion;
  onSelect: (v: HomepageVersion) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Homepage version"
      className="absolute right-3 top-3 z-30 flex items-center gap-1.5 rounded-full border border-white/25 bg-black/40 p-1.5 shadow-lg backdrop-blur-md sm:right-5 sm:top-5"
    >
      <span className="hidden pl-2 pr-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/60 sm:inline">
        Homepage
      </span>
      {VERSIONS.map((v) => (
        <button
          key={v.id}
          type="button"
          title={v.hint}
          aria-pressed={v.id === version}
          onClick={() => onSelect(v.id)}
          className={cn(
            "h-7 rounded-full px-2.5 text-[11px] font-medium transition-colors",
            v.id === version ? "bg-white text-black" : "text-white/75 hover:bg-white/20 hover:text-white",
          )}
        >
          {v.id} · {v.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------- section heading */

export function SectionHeading({
  kicker,
  title,
  lede,
  action,
  align = "left",
  tone = "default",
  wide = false,
  className,
}: {
  kicker: string;
  title: string;
  lede?: string;
  /** Optional right-hand link (desktop) — "Browse the catalogue →". */
  action?: { label: string; to: string };
  align?: "left" | "center";
  tone?: "default" | "inverse";
  /** Widen the heading box (max-w-4xl instead of max-w-2xl) so a longer
   *  title stays on one line at desktop widths; the lede keeps its measure. */
  wide?: boolean;
  className?: string;
}) {
  const inverse = tone === "inverse";
  return (
    <div
      className={cn(
        "mb-8 sm:mb-10",
        align === "center"
          ? "mx-auto max-w-2xl text-center"
          : "flex flex-wrap items-end justify-between gap-x-8 gap-y-4",
        className,
      )}
    >
      <div className={align === "center" ? "" : wide ? "max-w-4xl" : "max-w-2xl"}>
        <div className={cn("flex items-center gap-3", align === "center" && "justify-center")}>
          <span className={cn("h-px w-10", inverse ? "bg-white/60" : "brand-gradient")} />
          <span
            className={cn(
              "text-xs font-semibold uppercase tracking-[0.25em]",
              inverse ? "text-white/70" : "text-muted-foreground",
            )}
          >
            {kicker}
          </span>
          {align === "center" && (
            <span className={cn("h-px w-10", inverse ? "bg-white/60" : "brand-gradient")} />
          )}
        </div>
        <h2
          className={cn(
            "font-display mt-2 text-3xl font-semibold tracking-tight sm:text-4xl",
            inverse && "text-white",
          )}
        >
          {title}
        </h2>
        {lede && (
          <p
            className={cn(
              "mt-3 text-sm leading-relaxed sm:text-base",
              align !== "center" && "text-justify hyphens-auto",
              wide && "max-w-2xl",
              inverse ? "text-white/75" : "text-muted-foreground",
            )}
          >
            {lede}
          </p>
        )}
      </div>
      {action && align === "left" && (
        <TextLink to={action.to} label={action.label} className="hidden sm:inline-flex" />
      )}
    </div>
  );
}

export function TextLink({
  to,
  label,
  className,
}: {
  to: string;
  label: string;
  className?: string;
}) {
  const cls = cn(
    "inline-flex items-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-[color:var(--accent)]",
    className,
  );
  const body = (
    <>
      {label} <span aria-hidden>→</span>
    </>
  );
  // Template pages (/p/…) and in-page anchors are not typed routes; a plain
  // anchor keeps them honest.
  return to.startsWith("/p/") || to.startsWith("#") ? (
    <a href={to} className={cls}>
      {body}
    </a>
  ) : (
    <Link to={to} className={cls}>
      {body}
    </Link>
  );
}

/** Pill CTA from the /sample hero. `tone` picks white-on-dark or brand-on-light. */
export function PillLink({
  to,
  children,
  tone = "light",
  className,
}: {
  to: string;
  children: ReactNode;
  tone?: "light" | "dark" | "ghost";
  className?: string;
}) {
  const cls = cn(
    "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform hover:scale-[1.03]",
    tone === "light" && "bg-white text-black",
    tone === "dark" && "bg-primary text-primary-foreground",
    tone === "ghost" && "border border-white/40 text-white hover:bg-white/10",
    className,
  );
  return to.startsWith("/p/") || to.startsWith("#") ? (
    <a href={to} className={cls}>
      {children}
    </a>
  ) : (
    <Link to={to} className={cls}>
      {children}
    </Link>
  );
}

/* ----------------------------------------------------------- articles */

export function FeatureArticle({ article }: { article: NewsArticle }) {
  return (
    <a
      href={articleHref(article)}
      className="group flex flex-col overflow-hidden rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-xl lg:col-span-2"
    >
      {/* 16/9 while stacked; on large screens the graphic stretches so the
          card matches the height of the compact stories beside it. The
          picture is shown whole over a blurred copy of itself (P06-a, same
          fix as /news P27-a) and is never drawn taller than 420px, so small
          newsletter pictures are enlarged less: stacked, the frame itself is
          capped; on large screens the frame must still fill the card, so the
          picture is capped and centred and the blur fills the rest. */}
      <div className="relative aspect-[16/9] max-h-[420px] overflow-hidden bg-secondary lg:aspect-auto lg:max-h-none lg:min-h-64 lg:flex-1">
        <FramedPicture
          src={article.image_url || article.header_image_url || leadStoryImg}
          alt={article.image_alt ?? ""}
          className="transition-transform duration-[1200ms] ease-out group-hover:scale-105 lg:absolute lg:inset-0 lg:m-auto lg:max-h-[420px]"
        />
        <div className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-primary-foreground">
          Latest
        </div>
      </div>
      <div className="p-6 sm:p-8">
        <ArticleMeta article={article} />
        <h3 className="font-display mt-3 text-2xl font-bold leading-tight tracking-tight transition-colors group-hover:text-primary sm:text-3xl">
          {noWidow(article.title)}
        </h3>
        {article.excerpt && (
          <p className="mt-3 line-clamp-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base text-justify hyphens-auto">
            {article.excerpt}
          </p>
        )}
        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary">
          Read the story <span aria-hidden>→</span>
        </span>
      </div>
    </a>
  );
}

export function CompactArticle({
  article,
  wide,
  dense,
}: {
  article: NewsArticle;
  wide?: boolean;
  dense?: boolean;
}) {
  const img = article.image_url || article.header_image_url;
  return (
    <a
      href={articleHref(article)}
      className={cn(
        "group flex flex-1 overflow-hidden rounded-xl border bg-card transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg",
        dense ? "gap-3 p-2.5 sm:p-3" : "gap-4 p-3 sm:gap-5 sm:p-4",
      )}
    >
      {/* Portrait 3:4 thumbnail box matching the newsletter artwork (mostly
          300x400), so it is shown whole without a blurred backdrop; wide
          pictures are cropped at the sides instead (P06-b option B). */}
      <div
        className={cn(
          "relative aspect-[3/4] shrink-0 self-center overflow-hidden rounded-lg bg-secondary",
          wide ? "h-32 sm:h-36" : dense ? "w-24 sm:w-28" : "w-32 sm:w-40",
        )}
      >
        {img ? (
          <img
            src={img}
            alt={article.image_alt ?? ""}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="brand-gradient flex h-full w-full items-center justify-center font-display text-3xl font-semibold text-white/80">
            {(article.category ?? article.title).charAt(0)}
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-col justify-center">
        <ArticleMeta article={article} compact />
        <h3
          className={cn(
            "font-display line-clamp-2 font-semibold leading-snug transition-colors group-hover:text-primary",
            dense ? "mt-0.5 text-[15px] sm:text-base" : "mt-1 text-lg",
          )}
        >
          {noWidow(article.title)}
        </h3>
        {article.excerpt && (
          <p
            className={cn(
              "text-muted-foreground text-justify hyphens-auto",
              dense
                ? "mt-0.5 line-clamp-1 text-xs leading-relaxed"
                : "mt-1 line-clamp-2 text-xs leading-relaxed sm:text-sm",
            )}
          >
            {article.excerpt}
          </p>
        )}
      </div>
    </a>
  );
}

function ArticleMeta({ article, compact }: { article: NewsArticle; compact?: boolean }) {
  const date = formatArticleDate(article.published_at);
  return (
    <div
      className={cn(
        "flex items-center gap-2 uppercase tracking-wider text-muted-foreground",
        compact ? "text-[11px]" : "text-xs",
      )}
    >
      {article.category && <span className="font-semibold text-primary">{article.category}</span>}
      {article.category && date && <span aria-hidden>•</span>}
      {date && <span>{date}</span>}
    </div>
  );
}

/* ----------------------------------------------------------- research */

/** publish_date is free text; only trust it when it parses. */
function publishTime(p: ResearchProduct): number {
  const t = p.publish_date ? Date.parse(p.publish_date) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/** Newest study first, falling back to the catalogue order for undated rows. */
export function sortByNewest(products: ResearchProduct[]): ResearchProduct[] {
  return [...products].sort(
    (a, b) => publishTime(b) - publishTime(a) || a.display_order - b.display_order,
  );
}

/**
 * Month + Year formatting now lives in store.queries.ts (one helper for every
 * surface); re-exported so existing importers (HomepageV2) keep working.
 */
export { formatPublishDate };

export function stripHtml(html: string | null): string {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

export function ResearchCover({
  product,
  className,
}: {
  product: ResearchProduct;
  className?: string;
}) {
  return (
    <div className={cn("relative overflow-hidden bg-secondary", className)}>
      {product.image_url ? (
        <img
          src={product.image_url}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
      ) : (
        <div className="brand-gradient flex h-full w-full items-end p-4">
          <span className="font-display text-lg font-semibold leading-tight text-white">
            {product.title}
          </span>
        </div>
      )}
    </div>
  );
}

export function ResearchCard({ product }: { product: ResearchProduct }) {
  const meta = [product.pages ? `${product.pages} pages` : null, formatPublishDate(product.publish_date)]
    .filter(Boolean)
    .join(" · ");
  return (
    <Link
      to="/research"
      hash={product.handle}
      className="group flex flex-col overflow-hidden rounded-xl border bg-card transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg"
    >
      <ResearchCover product={product} className="aspect-[4/3]" />
      <div className="flex flex-1 flex-col p-5">
        {meta && (
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{meta}</div>
        )}
        <h3 className="font-display mt-1 text-lg font-semibold leading-snug transition-colors group-hover:text-primary">
          {product.title}
        </h3>
        <div className="mt-auto flex items-center justify-between pt-4">
          <span className="text-sm font-semibold">
            {formatPrice(product.price_cents, product.currency)}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
            View study <span aria-hidden>→</span>
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------- awards */

export function useAwards(): AiaAward[] {
  const { data } = useQuery(aiaAwardsQueryOptions());
  return useMemo(
    () =>
      (data ?? []).filter((a) => a.is_active).sort((a, b) => a.display_order - b.display_order),
    [data],
  );
}

export function AwardCard({ award }: { award: AiaAward }) {
  const portrait = award.category === "leadership";
  const inner = (
    <>
      <div className="flex items-start gap-4">
        {award.image_path ? (
          <img
            src={award.image_path}
            alt=""
            loading="lazy"
            className={cn(
              "h-16 w-16 shrink-0",
              portrait ? "rounded-full object-cover" : "rounded-md bg-white object-contain p-1",
            )}
          />
        ) : (
          <div className="brand-gradient h-16 w-16 shrink-0 rounded-md" />
        )}
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-primary">
            {portrait ? "Leadership award" : "Industry award"}
          </div>
          <h3 className="font-display mt-1 text-lg font-semibold leading-snug transition-colors group-hover:text-primary">
            {award.name}
          </h3>
        </div>
      </div>
      {award.description && (
        <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-muted-foreground text-left">
          {award.description}
        </p>
      )}
    </>
  );
  const cls =
    "group flex flex-col rounded-xl border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg";
  return award.article_url ? (
    <StoredLink url={award.article_url} className={cls}>
      {inner}
    </StoredLink>
  ) : (
    <Link to="/awards" className={cls}>
      {inner}
    </Link>
  );
}

/* -------------------------------------------------------------- board */

export function useBoard(): AiaMember[] {
  const { data } = useQuery(boardRosterQueryOptions());
  return useMemo(
    () =>
      (data ?? [])
        .filter((m) => m.is_active)
        .sort(
          (a, b) =>
            a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name),
        ),
    [data],
  );
}

function initials(m: AiaMember) {
  return `${m.first_name.charAt(0)}${m.last_name.charAt(0)}`.toUpperCase();
}

export function BoardMember({ member }: { member: AiaMember }) {
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3 transition-colors hover:border-primary/30">
      <Avatar className="h-12 w-12 shrink-0">
        {member.photo_url ? (
          <AvatarImage src={member.photo_url} alt={formatFullName(member)} />
        ) : null}
        <AvatarFallback className="bg-primary/10 text-xs text-primary">
          {initials(member)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="truncate text-[0.95rem] font-medium leading-tight">
          {formatFullName(member)}
        </div>
        <div className="line-clamp-2 text-[0.8125rem] leading-snug text-muted-foreground">
          {[member.title, member.org].filter(Boolean).join(" / ")}
        </div>
      </div>
    </div>
  );
}
